import type { RequestResponse } from '@/types/publicType'
import type {
  roomInfoInterface,
  membersInterface,
  roomEndLiveInterface,
  allRankGetGuardRankInterface,
  statUserRankInterface,
  roomMicApplyListInterface,
  roomRocketConfigInterface,
  roomRocketDrawInterface,
  roomLevelRunningInfoInterface,
  roomLevelMedalPicsInterface
} from '@/types/apiType/roomType'

/**
 * 获取房间信息
 * @param {number} unionid
 */
export function apiGetRoomInfo(params: {
  roomId: number | string
}): Promise<RequestResponse<roomInfoInterface>> {
  return getFetchData({
    url: '/h5/r/entry',
    method: 'GET',
    params
  })
}

/**
 * 获取房间在线成员列表
 * @param {number} unionid
 */
export function apiGetMemberList(data: {
  room_id: number | string
  page: number
  pageSize: number
}): Promise<RequestResponse<membersInterface>> {
  return getFetchData({
    url: '/h5/r/members',
    method: 'POST',
    data
  })
}

/**
 * 获取腾讯渠道key
 */
export function apiGetTencenKey(): Promise<RequestResponse<void>> {
  return getFetchData({
    url: '/h5/i/tencentKey',
    method: 'POST'
  })
}

/**
 * 锁麦/开麦操作
 * @param {number} position 麦序位置
 * @param {number} roomUid 房主UID
 * @param {number} state 状态，1：锁麦，0开麦（即取消锁麦）
 * @param {number} type 房间类型
 */
export function apiLockMic(data: {
  position: number
  roomUid: number
  state: number
  type: number
}): Promise<RequestResponse<void>> {
  return getFetchData({
    url: '/h5/room/mic/lockmic',
    method: 'POST',
    data
  })
}

/**
 * 锁坑位/取消锁
 * @param {number} position 麦序位置
 * @param {number} roomId 房主UID
 * @param {number} state 状态 1：锁坑位，0取消锁
 */
export function apiLockPos(params: {
  position: number
  roomId: number
  state: number
}): Promise<RequestResponse<void>> {
  return getFetchData({
    url: '/h5/room/mic/v2/lockpos',
    method: 'GET',
    params
  })
}

/**
 * 踢出房间
 * @param {number} roomId 房间id
 * @param {number} targetUid 目标用户uid
 */
export function apikikUser(params: {
  roomId: number
  targetUid: number
}): Promise<RequestResponse<void>> {
  return getFetchData({
    url: '/h5/r/kick/user',
    method: 'GET',
    params
  })
}

/**
 * 结束直播接口
 * @param {number} roomId 房间id
 * @param {number} callId 通话ID
 */
export function apiRoomEndLive(data: {
  roomId: number
  callId?: number
}): Promise<RequestResponse<roomEndLiveInterface>> {
  return getFetchData({
    url: '/room/endLive',
    method: 'POST',
    data,
    loading: true
  })
}

/**
 * 意见反馈
 * @param {string} feedbackDesc 内容
 */
export function apiH5Feedback(data: {
  feedbackDesc: string
}): Promise<RequestResponse<void>> {
  return getFetchData({
    url: '/h5/feedback',
    method: 'POST',
    data,
    loading: true
  })
}

/**
 * 发送弹幕V1
 * @param {number} room_id 房间id
 * @param {string} barrage_msg 发送内容 base64.encode
 * @param {number} moduleId 1v1的时候 加个字段 moduleId, 其实就是callId,其他场景默认0
 */
export function apiImroomBarrageSend(data: {
  room_id: number
  barrage_msg: string
  moduleId: number
}): Promise<RequestResponse<void>> {
  return getFetchData({
    url: '/imroom/v1/h5/barrage/sendV1',
    method: 'POST',
    data,
    loading: true
  })
}

/**
 * 获取粉丝排行榜
 * @param {number} pageSize 页数
 * @param {number} offset 偏移量
 * @param {boolean} lastWeek 是否是上周
 * @param {number} targetUid 查询uid
 */
export function apiAllRankGetGuardRank(params: {
  pageSize: number
  offset: number
  lastWeek: boolean
  targetUid: number
}): Promise<RequestResponse<allRankGetGuardRankInterface>> {
  return getFetchData({
    url: '/allrank/getGuardRank',
    method: 'GET',
    params,
    loading: true
  })
}

/**
 * 查询个人贡献榜，分日、周、总榜
 * @param {1|2|3} dateType 1，日；2，周；3，总榜
 * @param {number} roomId 房间ID
 * @param {number} roomUid 房间UID
 */
export function apiStatUserRank(params: {
  dateType: 1 | 2 | 3
  roomId: number
  roomUid: number
}): Promise<RequestResponse<statUserRankInterface[]>> {
  return getFetchData({
    url: '/stat/user/rankh5',
    method: 'GET',
    params,
    loading: true
  })
}
/**
 * 语言房
 * 查询个人贡献榜，分日、周、总榜
 * @param {1|2} rankType 排行榜类型：1魅力榜；2财富榜
 * @param {1|2|3} dateType 1，日；2，周；3，总榜
 * @param {number} roomId 房间ID
 * @param {number} roomUid 房间UID
 */
export function apiStatUserVoiceRank(params: {
  rankType: 1 | 2
  dateType: 1 | 2 | 3
  roomId: number
  roomUid: number
}): Promise<RequestResponse<statUserRankInterface[]>> {
  return getFetchData({
    url: '/stat/user/voice/rank-h5',
    method: 'GET',
    params,
    loading: true
  })
}

/**
 * 设置/取消房间管理员
 * @param {number} account 目标用户uid
 * @param {1|2} is_add 1=添加、0=移除
 * @param {number} room_id 房间id
 */
export function apiMarkChatRoomManager(data: {
  account: number
  room_id: number
  is_add: 1 | 0
}): Promise<RequestResponse<statUserRankInterface[]>> {
  return getFetchData({
    url: '/chic/h5/room/markChatRoomManager',
    method: 'POST',
    data
  })
}

/**
 * 房间排麦列表
 * @param {number} roomId
 * @param {number} pageNum
 * @param {number} pageSize
 */
export function apiRoomMicApplyList(params: {
  roomId: number
  pageNum: number
  pageSize: number
}): Promise<RequestResponse<roomMicApplyListInterface>> {
  return getFetchData({
    url: '/chic/h5/room/mic/apply/list',
    method: 'GET',
    params
  })
}

/**
 * 排麦申请
 * @param {number} roomId
 * @param {1|2} type 0=语音、1=视频
 */
export function apiRoomMicApply(data: {
  roomId: number
  type: 0 | 1
}): Promise<RequestResponse<void>> {
  return getFetchData({
    url: '/chic/h5/room/mic/apply',
    method: 'POST',
    data,
    loading: true
  })
}

/**
 * 排麦申请取消
 * @param {number} roomId
 */
export function apiRoomMicCancel(data: {
  roomId: number
}): Promise<RequestResponse<void>> {
  return getFetchData({
    url: '/chic/h5/room/mic/cancel',
    method: 'POST',
    data,
    loading: true
  })
}

/**
 * 排麦 同意上麦申请
 * @param {number} roomId
 * @param {number} tgUid 同意目标 UID
 */
export function apiRoomMicAgree(data: {
  roomId: number
  tgUid: number
}): Promise<RequestResponse<void>> {
  return getFetchData({
    url: '/chic/h5/room/mic/agree',
    method: 'POST',
    data,
    loading: true
  })
}

/**
 * 排麦 拒绝上麦申请
 * @param {number} roomId
 * @param {number} tgUid 同意目标 UID
 */
export function apiRoomMicReject(data: {
  roomId: number
  tgUid: number
}): Promise<RequestResponse<void>> {
  return getFetchData({
    url: '/chic/h5/room/mic/reject',
    method: 'POST',
    data,
    loading: true
  })
}

/**
 * 排麦 拒绝全部上麦申请
 * @param {number} roomId
 */
export function apiRoomMicRejectAll(data: {
  roomId: number
}): Promise<RequestResponse<void>> {
  return getFetchData({
    url: '/chic/h5/room/mic/rejectAll',
    method: 'POST',
    data,
    loading: true
  })
}

/**
 * 房间炸弹配置
 * @param {number} roomUid
 * @param {number} roomType
 */
export function apiRoomRocketGetConfig(data: {
  sn: 123456
  roomUid: number
  roomType: number
}): Promise<RequestResponse<roomRocketConfigInterface>> {
  return getFetchData({
    url: '/chic/h5/room/rocket/getConfig',
    method: 'POST',
    data
  })
}

/**
 * 房间炸弹奖励领取
 * @param {number} roomUid
 * @param {number} roomType
 */
export function apiRoomRocketDraw(data: {
  roomUid: number
  roomType: number
}): Promise<RequestResponse<roomRocketDrawInterface>> {
  return getFetchData({
    url: '/chic/h5/room/rocket/draw',
    method: 'POST',
    data,
    loading: true
  })
}

/**
 * 获取房间等级
 * @param {number} roomId
 */
export function apiRoomLevelRunningInfo(params: {
  roomId: number
}): Promise<RequestResponse<roomLevelRunningInfoInterface>> {
  return getFetchData({
    url: '/chic/h5/room/level/running/info',
    method: 'GET',
    params
  })
}

/**
 * 获取房间等级勋章图片集合
 */
export function apiRoomLevelMedalPics(): Promise<
  RequestResponse<roomLevelMedalPicsInterface[]>
> {
  return getFetchData({
    url: '/chic/h5/room/level/medalPics',
    method: 'GET'
  })
}
